/**
 * InventoryDocumentPrintView
 *
 * A4-sized, RTL Hebrew document template for inventory documents
 * (invoices, delivery notes, purchase orders, etc.).
 *
 * Designed to be rendered inside a React ref and passed to react-to-print
 * so the browser's native print-to-PDF engine handles the output.
 *
 * Usage:
 *   const printRef = useRef<HTMLDivElement>(null);
 *   const handlePrint = useReactToPrint({ contentRef: printRef });
 *
 *   <InventoryDocumentPrintView ref={printRef} data={printData} />
 *   <button onClick={handlePrint}>הדפס / שמור PDF</button>
 */

import { forwardRef } from 'react';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface InventoryPrintLine {
  product_id?: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  discount_pct?: number;
  line_total: number;
}

export interface InventoryPrintData {
  companyName?: string;
  docTypeName: string;
  docNumber: string | number;
  date: string;        // ISO string – rendered as DD/MM/YYYY
  clientName?: string;
  lines: InventoryPrintLine[];
  subtotal: number;
  vatAmount: number;
  grandTotal: number;
  vatRate?: number;    // e.g. 18
  remarks?: string;
  documentId?: string;
  clientId?: string;
  issuedByName?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    // Guard against invalid dates
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('he-IL', {
      day:   '2-digit',
      month: '2-digit',
      year:  'numeric',
    });
  } catch {
    return iso;
  }
};

const fmtCurrency = (n: number): string =>
  `₪${n.toLocaleString('he-IL', {
    minimumFractionDigits:  2,
    maximumFractionDigits: 2,
  })}`;

// ─── Component ─────────────────────────────────────────────────────────────────

export const InventoryDocumentPrintView = forwardRef<
  HTMLDivElement,
  { data: InventoryPrintData }
>(function InventoryDocumentPrintView({ data }, ref) {
  const hasDiscount = data.lines.some(
    (l) => l.discount_pct != null && l.discount_pct > 0
  );
  const vatLabel = data.vatRate != null ? `מע״מ ${data.vatRate}%` : 'מע״מ';
  const totalQty = data.lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const totalSkus = new Set(data.lines.map((l) => l.product_id || l.product_name)).size;

  return (
    <>
      {/*
        ── Print-only styles injected via a <style> tag so they work regardless
        of Tailwind purging, and survive react-to-print's iframe approach.
      */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800&display=swap');

        .inv-doc-root {
          font-family: 'Heebo', 'Arial Hebrew', 'Arial', sans-serif;
          direction: rtl;
          background: #ffffff;
          color: #111827;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          padding: 16mm 18mm 24mm 18mm;
          box-sizing: border-box;
          position: relative;
        }

        /* Screen preview card shadow */
        @media screen {
          .inv-doc-root {
            box-shadow: 0 4px 32px rgba(0,0,0,0.12);
            border-radius: 4px;
          }
        }

        @media print {
          @page { size: A4 portrait; margin: 0; }
          .inv-doc-root {
            width: 210mm;
            min-height: 297mm;
            padding: 16mm 18mm 24mm 18mm;
            box-shadow: none;
            border-radius: 0;
          }
          .inv-no-print { display: none !important; }
        }

        /* ── Header ─────────────────────────────────────────────────── */
        .inv-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding-bottom: 8mm;
          border-bottom: 2.5px solid #2563eb;
        }
        .inv-company-name {
          font-size: 22pt;
          font-weight: 800;
          color: #2563eb;
          line-height: 1.1;
        }
        .inv-doc-title {
          font-size: 17pt;
          font-weight: 700;
          color: #111827;
          margin: 0;
          text-align: left;
        }
        .inv-doc-subtitle {
          font-size: 8pt;
          color: #6b7280;
          margin: 3px 0 0;
          direction: ltr;
          text-align: left;
        }

        /* ── Meta grid ──────────────────────────────────────────────── */
        .inv-meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5mm;
          margin: 8mm 0 6mm;
        }
        .inv-meta-box {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 4mm 5mm;
        }
        .inv-meta-label {
          font-size: 7pt;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #9ca3af;
          margin-bottom: 2px;
        }
        .inv-meta-value {
          font-size: 11pt;
          font-weight: 600;
          color: #111827;
        }
        .inv-meta-value-large {
          font-size: 14pt;
          font-weight: 800;
          color: #2563eb;
        }

        /* ── Items table ────────────────────────────────────────────── */
        .inv-table {
          width: 100%;
          border-collapse: collapse;
          margin: 4mm 0;
        }
        .inv-table thead tr {
          border-bottom: 2px solid #2563eb;
        }
        .inv-table thead th {
          padding: 3mm 3mm;
          font-size: 8.5pt;
          font-weight: 700;
          text-align: right;
          color: #374151;
        }
        .inv-table thead th.inv-num {
          text-align: center;
        }
        .inv-table thead th.inv-right {
          text-align: left;
        }
        .inv-table tbody tr {
          border-bottom: 1px solid #f3f4f6;
        }
        .inv-table tbody tr:last-child {
          border-bottom: 2px solid #e5e7eb;
        }
        .inv-table tbody tr:nth-child(even) {
          background: #f9fafb;
        }
        .inv-table tbody td {
          padding: 2.5mm 3mm;
          font-size: 9.5pt;
          vertical-align: middle;
        }
        .inv-table tbody td.inv-num {
          text-align: center;
          font-weight: 600;
        }
        .inv-table tbody td.inv-right {
          text-align: left;
          font-weight: 600;
        }
        .inv-table tfoot td {
          padding: 2mm 3mm;
          font-size: 9pt;
        }

        /* ── Totals ─────────────────────────────────────────────────── */
        .inv-totals-wrap {
          display: flex;
          justify-content: flex-start;
          margin-top: 5mm;
        }
        .inv-totals-box {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
          min-width: 68mm;
        }
        .inv-totals-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 2.5mm 5mm;
          gap: 10mm;
          border-bottom: 1px solid #f3f4f6;
          font-size: 9pt;
        }
        .inv-totals-row:last-child {
          border-bottom: none;
        }
        .inv-totals-row.inv-grand {
          background: #eff6ff;
          font-weight: 700;
          font-size: 10.5pt;
        }
        .inv-totals-label {
          color: #6b7280;
          white-space: nowrap;
        }
        .inv-totals-value {
          font-weight: 600;
          white-space: nowrap;
        }
        .inv-totals-row.inv-grand .inv-totals-label {
          color: #1d4ed8;
          font-weight: 700;
        }
        .inv-totals-row.inv-grand .inv-totals-value {
          color: #2563eb;
          font-size: 12pt;
        }

        /* ── Remarks ────────────────────────────────────────────────── */
        .inv-remarks-box {
          margin-top: 6mm;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 3mm 5mm;
        }
        .inv-remarks-label {
          font-size: 7pt;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #9ca3af;
          margin-bottom: 2px;
        }
        .inv-remarks-text {
          font-size: 9pt;
          color: #374151;
          line-height: 1.6;
          white-space: pre-wrap;
        }

        /* ── Footer ─────────────────────────────────────────────────── */
        .inv-footer {
          position: absolute;
          bottom: 10mm;
          right: 18mm;
          left: 18mm;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 3mm;
          border-top: 1px solid #e5e7eb;
        }
        .inv-footer-text {
          font-size: 7.5pt;
          color: #9ca3af;
        }
      `}</style>

      <div className="inv-doc-root" ref={ref}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="inv-header">
          {/* Right: company name */}
          <div>
            {data.companyName ? (
              <div className="inv-company-name">{data.companyName}</div>
            ) : (
              <div className="inv-company-name" style={{ color: '#9ca3af' }}>
                שם החברה
              </div>
            )}
          </div>

          {/* Left: document type title */}
          <div style={{ textAlign: 'left' }}>
            <p className="inv-doc-title">{data.docTypeName}</p>
            <p className="inv-doc-subtitle">Tax Invoice / Document</p>
          </div>
        </div>

        {/* ── Meta grid ───────────────────────────────────────────────────── */}
        <div className="inv-meta-grid">

          {/* Document number */}
          <div className="inv-meta-box" style={{ borderColor: '#bfdbfe' }}>
            <div className="inv-meta-label">מספר מסמך</div>
            <div className="inv-meta-value-large">{data.docNumber}</div>
          </div>

          {/* Date */}
          <div className="inv-meta-box">
            <div className="inv-meta-label">תאריך</div>
            <div className="inv-meta-value">{fmtDate(data.date)}</div>
          </div>

          {/* Client */}
          <div className="inv-meta-box" style={{ gridColumn: '1 / -1' }}>
            <div className="inv-meta-label">לקוח</div>
            <div className="inv-meta-value">
              {data.clientName || '—'}
            </div>
          </div>
        </div>

        {/* ── Items table ─────────────────────────────────────────────────── */}
        <table className="inv-table">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>#</th>
              <th style={{ width: hasDiscount ? '38%' : '46%' }}>שם הפריט</th>
              <th className="inv-num" style={{ width: '12%' }}>כמות</th>
              <th className="inv-num" style={{ width: '15%' }}>מחיר ליח׳</th>
              {hasDiscount && (
                <th className="inv-num" style={{ width: '12%' }}>הנחה</th>
              )}
              <th className="inv-right" style={{ width: '19%' }}>סה״כ שורה</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, idx) => (
              <tr key={idx}>
                <td style={{ color: '#9ca3af', fontSize: '8pt' }}>{idx + 1}</td>
                <td style={{ fontWeight: 500 }}>{line.product_name}</td>
                <td className="inv-num" style={{ color: '#2563eb', fontSize: '11pt', fontWeight: 700 }}>
                  {line.quantity.toLocaleString('he-IL')}
                </td>
                <td className="inv-num" style={{ color: '#374151' }}>
                  {fmtCurrency(line.unit_price)}
                </td>
                {hasDiscount && (
                  <td className="inv-num" style={{ color: '#6b7280' }}>
                    {line.discount_pct != null && line.discount_pct > 0
                      ? `${line.discount_pct}%`
                      : '—'}
                  </td>
                )}
                <td className="inv-right">
                  {fmtCurrency(line.line_total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Totals ──────────────────────────────────────────────────────── */}
        <div className="inv-totals-wrap">
          <div className="inv-totals-box">
            <div className="inv-totals-row">
              <span className="inv-totals-label">סה״כ פריטים</span>
              <span className="inv-totals-value">{totalQty.toLocaleString('he-IL', { maximumFractionDigits: 3 })}</span>
            </div>
            <div className="inv-totals-row">
              <span className="inv-totals-label">סה״כ מק״טים</span>
              <span className="inv-totals-value">{totalSkus}</span>
            </div>
            <div className="inv-totals-row">
              <span className="inv-totals-label">סכום לפני מע״מ</span>
              <span className="inv-totals-value">{fmtCurrency(data.subtotal)}</span>
            </div>
            <div className="inv-totals-row">
              <span className="inv-totals-label">{vatLabel}</span>
              <span className="inv-totals-value">{fmtCurrency(data.vatAmount)}</span>
            </div>
            <div className="inv-totals-row inv-grand">
              <span className="inv-totals-label">סה״כ לתשלום</span>
              <span className="inv-totals-value">{fmtCurrency(data.grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* ── Remarks ─────────────────────────────────────────────────────── */}
        {data.remarks && data.remarks.trim() !== '' && (
          <div className="inv-remarks-box">
            <div className="inv-remarks-label">הערות</div>
            <div className="inv-remarks-text">{data.remarks}</div>
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="inv-footer">
          <span className="inv-footer-text">
            {data.docTypeName} · {data.docNumber}
          </span>
          {data.issuedByName && (
            <span className="inv-footer-text">
              הופק ע״י: {data.issuedByName}
            </span>
          )}
          <span className="inv-footer-text">
            הופק: {fmtDate(new Date().toISOString())}
          </span>
        </div>

      </div>
    </>
  );
});
